import * as React from 'react';
import PropTypes from 'prop-types';
import * as errors from './errors.js';
export { errors as Errors };
export { Loader } from './loader.js';
import { LoaderContext, TranslateContext } from './context.js';
export { Type as SourceType } from './source.js';
export { useTranslate } from './use-translate.js';

/**
 * React Context provider for translation loader
 *
 * Enables content fetching for other parts of i18n API thus must be wrapping
 * {@link TranslationProvider} and components that use {@link useTranslate}
 * hook.
 *
 * @example
 * const loader = Herz.I18n.Loader({ /* loader config *\/ })
 *
 * ReactDOM.render(
 *   <Herz.I18n.LoaderProvider loader={loader}>
 *     <App />
 *   </Herz.I18n.LoaderProvider>
 * )
 *
 * @param {Object} props Component props
 * @param {!Loader} props.loader Translation loader instance
 *
 * @access public
 */

function LoaderProvider(props) {
  return /*#__PURE__*/React.createElement(LoaderContext.Provider, {
    value: props.loader
  }, props.children);
}
LoaderProvider.propTypes = {
  children: PropTypes.node,
  loader: PropTypes.object.isRequired
};

function debugTranslate(key) {
  return "[".concat(key, "]");
}
/**
 * React Context provider for translation APIs
 *
 * @example
 * function TranslationProvider(props) {
 *   const globals = React.useMemo(() => ({
 *     brandName: props.brandName,
 *     supportEmail: props.supportEmail,
 *   }), [props.brandName, props.supportEmail])
 *
 *   const processors = React.useMemo(() => ({
 *     currency: createCurrencyProcessor(props.currency),
 *     uppercase: R.toUpper,
 *   }), [props.currency])
 *
 *   return (
 *     <Herz.I18n.TranslationProvider
 *       globals={globals}
 *       language="en-DE"
 *       bucket="legendz"
 *       processors={processors}
 *     >
 *       {props.children}
 *     </Herz.I18n.TranslationProvider>
 *   )
 * }
 *
 * @param {Object} props Component props
 * @param {?Object.<string, *>} props.globals Global variables that will be
 * available for every key. Ensure passed object is **memoized** (or is static)
 * to avoid potential rendering loops.
 * @param {!string|!Array.<!string>} props.language Language to fetch
 * translations in
 * @param {string} props.bucket Bucket to fetch translations from
 * @param {?Object.<string, function>} props.processors Variable processors
 * that will be available for every key. Ensure passed object is **memoized**
 * (or is static) to avoid potential rendering loops.
 * @param {("all"|"missing")} props.debug If present, this will provide some
 * debugging help for translation keys. Passing "all" will show all translations
 * as keys. "missing" will show only the keys where content is missing,
 * otherwise translated content will be shown. In the absence of the `debug`
 * flag or it's value being "off", the standard `getTranslate` functionality will
 * be used and thus all keys will be translated, or return null if the key does
 * not exist.
 *
 * @access public
 */


function TranslationProvider(props) {
  var loader = React.useContext(LoaderContext); // use useMemo because useEffect does not run on server

  var getTranslate = React.useMemo(() => {
    loader.init({
      globals: props.globals,
      language: props.language,
      bucket: props.bucket,
      processors: props.processors
    });

    if (props.debug === "all") {
      return function getDebugTranslate() {
        return debugTranslate;
      };
    }

    if (props.debug === "missing") {
      return function getDebugTranslate(keys) {
        var translate = loader.getTranslate(keys);
        return function _debugTranslate(key, variables) {
          return translate(key, variables) || debugTranslate(key);
        };
      };
    }

    return loader.getTranslate;
  }, [props.debug, props.globals, props.language, props.bucket, props.processors, loader]);
  return /*#__PURE__*/React.createElement(TranslateContext.Provider, {
    value: getTranslate
  }, props.children);
}
TranslationProvider.defaultProps = {
  globals: {},
  processors: {}
};
TranslationProvider.propTypes = {
  children: PropTypes.node,
  debug: PropTypes.oneOf(["missing", "all", "off"]),
  globals: PropTypes.object,
  language: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]).isRequired,
  bucket: PropTypes.string,
  loader: PropTypes.shape({
    getTranslate: PropTypes.func.isRequired
  }),
  processors: PropTypes.object
};

export { LoaderProvider, TranslationProvider };
